import React, { useState, useEffect } from 'react';
import { X, UserX, UserCheck, Trash2, Clock, Tag, UserPlus, Users, CalendarClock, Check } from 'lucide-react';
import { dataRoomAPI } from '../../../services/api';

export default function MembersPanel({ open, onClose, dataRoomId, onOpenInvite, onOpenMatrix }) {
  const [tab, setTab] = useState('external');
  const [externalUsers, setExternalUsers] = useState([]);
  const [internalMembers, setInternalMembers] = useState([]);
  const [amcUsers, setAmcUsers] = useState([]);
  const [selectedAmcUser, setSelectedAmcUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingExpiry, setEditingExpiry] = useState(null); // user id being edited
  const [newExpiry, setNewExpiry] = useState('');
  const [savingExpiry, setSavingExpiry] = useState(false);

  useEffect(() => {
    if (open) load();
  }, [open, tab]);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'external') {
        const r = await dataRoomAPI.listExternalUsers(dataRoomId);
        setExternalUsers(r.data.data || []);
      } else {
        const [m, a] = await Promise.all([
          dataRoomAPI.listInternalMembers(dataRoomId),
          dataRoomAPI.availableAmcUsers(dataRoomId),
        ]);
        setInternalMembers(m.data.data || []);
        setAmcUsers(a.data.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke access for this user?')) return;
    await dataRoomAPI.revokeExternalUser(dataRoomId, id);
    load();
  };

  const handleReinstate = async (id) => {
    await dataRoomAPI.reinstateExternalUser(dataRoomId, id);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this user from the data room?')) return;
    await dataRoomAPI.deleteExternalUser(dataRoomId, id);
    load();
  };

  const handleTag = async () => {
    if (!selectedAmcUser) return;
    try {
      await dataRoomAPI.tagInternalMember(dataRoomId, { user_id: selectedAmcUser });
      setSelectedAmcUser('');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleUntag = async (memberId) => {
    if (!window.confirm('Untag this member?')) return;
    await dataRoomAPI.untagInternalMember(dataRoomId, memberId);
    load();
  };

  const startEditExpiry = (user) => {
    setEditingExpiry(user.id);
    // Pre-fill with current value in datetime-local format
    if (user.expires_at) {
      try {
        const dt = new Date(user.expires_at);
        const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        setNewExpiry(local);
      } catch {
        setNewExpiry('');
      }
    } else {
      setNewExpiry('');
    }
  };

  const saveExpiry = async (userId) => {
    if (!newExpiry) return;
    setSavingExpiry(true);
    try {
      const isoDate = new Date(newExpiry).toISOString();
      await dataRoomAPI.updateExternalUser(dataRoomId, userId, { expires_at: isoDate });
      setEditingExpiry(null);
      setNewExpiry('');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingExpiry(false);
    }
  };

  if (!open) return null;

  const statusBadge = (u) => {
    if (u.effective_status === 'revoked') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">REVOKED</span>;
    if (u.effective_status === 'expired') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">EXPIRED</span>;
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">ACTIVE</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="members-panel">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Data Room Access</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="border-b px-5">
          <div className="flex gap-4">
            <button
              onClick={() => setTab('external')}
              className={`py-2.5 text-sm font-medium border-b-2 ${tab === 'external' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'}`}
              data-testid="tab-external"
            >
              <Users size={14} className="inline mr-1" /> External Users ({externalUsers.length})
            </button>
            <button
              onClick={() => setTab('internal')}
              className={`py-2.5 text-sm font-medium border-b-2 ${tab === 'internal' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'}`}
              data-testid="tab-internal"
            >
              <Tag size={14} className="inline mr-1" /> AMC Members ({internalMembers.length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {tab === 'external' ? (
            <>
              <div className="flex justify-between mb-3">
                <p className="text-xs text-gray-500">Users invited to access this data room (separate from AMC user management)</p>
                <div className="flex gap-2">
                  <button onClick={onOpenMatrix} className="px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50" data-testid="open-matrix-btn">
                    View Matrix
                  </button>
                  <button onClick={onOpenInvite} className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700" data-testid="invite-btn">
                    <UserPlus size={12} className="mr-1" /> Invite User
                  </button>
                </div>
              </div>
              {loading ? (
                <div className="py-6 text-center text-gray-500 text-sm">Loading...</div>
              ) : (
                <table className="w-full text-sm" data-testid="external-users-table">
                  <thead>
                    <tr className="border-b text-left text-gray-600 text-xs uppercase">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2">Access Expires</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 w-32"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {externalUsers.map(u => (
                      <tr key={u.id} className="border-b hover:bg-gray-50" data-testid={`eu-row-${u.id}`}>
                        <td className="px-3 py-2 font-medium">{u.name}</td>
                        <td className="px-3 py-2 text-gray-600">{u.email}</td>
                        <td className="px-3 py-2 text-gray-600">{u.role_name}</td>
                        <td className="px-3 py-2 text-xs">
                          {editingExpiry === u.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="datetime-local"
                                value={newExpiry}
                                onChange={e => setNewExpiry(e.target.value)}
                                className="border rounded px-1.5 py-1 text-xs w-44"
                                data-testid={`expiry-input-${u.id}`}
                              />
                              <button
                                onClick={() => saveExpiry(u.id)}
                                disabled={savingExpiry || !newExpiry}
                                className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-40"
                                title="Save"
                                data-testid={`save-expiry-${u.id}`}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={() => { setEditingExpiry(null); setNewExpiry(''); }}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Cancel"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              {u.expires_at ? (
                                <>
                                  <Clock size={11} className="text-gray-400" />
                                  <span className="text-gray-600">{new Date(u.expires_at).toLocaleString()}</span>
                                </>
                              ) : (
                                <span className="text-gray-400">No expiry</span>
                              )}
                              {!u.is_revoked && (
                                <button
                                  onClick={() => startEditExpiry(u)}
                                  className="p-0.5 text-blue-500 hover:text-blue-700 ml-1"
                                  title="Change access date"
                                  data-testid={`edit-expiry-${u.id}`}
                                >
                                  <CalendarClock size={13} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">{statusBadge(u)}</td>
                        <td className="px-3 py-2 text-right">
                          {u.is_revoked ? (
                            <button onClick={() => handleReinstate(u.id)} className="p-1 text-emerald-500 hover:text-emerald-700" title="Reinstate" data-testid={`reinstate-${u.id}`}>
                              <UserCheck size={14} />
                            </button>
                          ) : (
                            <button onClick={() => handleRevoke(u.id)} className="p-1 text-amber-500 hover:text-amber-700" title="Revoke" data-testid={`revoke-${u.id}`}>
                              <UserX size={14} />
                            </button>
                          )}
                          <button onClick={() => handleDelete(u.id)} className="p-1 text-gray-400 hover:text-red-500" title="Delete" data-testid={`delete-eu-${u.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {externalUsers.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-6 text-gray-500">No external users yet</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <select
                  value={selectedAmcUser}
                  onChange={e => setSelectedAmcUser(e.target.value)}
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                  data-testid="select-amc-user"
                >
                  <option value="">Select AMC user to tag...</option>
                  {amcUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                </select>
                <button onClick={handleTag} disabled={!selectedAmcUser}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="tag-btn">
                  <UserPlus size={14} className="mr-1" /> Tag
                </button>
              </div>
              {loading ? (
                <div className="py-6 text-center text-gray-500 text-sm">Loading...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600 text-xs uppercase">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {internalMembers.map(m => (
                      <tr key={m.id} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{m.user_name}</td>
                        <td className="px-3 py-2 text-gray-600">{m.user_email}</td>
                        <td className="px-3 py-2 text-gray-600 capitalize">{m.user_role || '--'}</td>
                        <td className="px-3 py-2">
                          <button onClick={() => handleUntag(m.id)} className="p-1 text-gray-400 hover:text-red-500" title="Untag" data-testid={`untag-${m.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {internalMembers.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-6 text-gray-500">No AMC members tagged yet</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
